import { supabase } from "@/integrations/supabase/client";
import { EmailEvent } from "@/lib/types";

export interface AnalyticsEmailEvent extends EmailEvent {
  mailbox_email?: string;
}

export interface AnalyticsFilters {
  mailboxId?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export const fetchEmailEvents = async (
  tenantId: string,
  page: number = 1,
  pageSize: number = 25,
  filters: AnalyticsFilters = {}
): Promise<PaginatedResponse<AnalyticsEmailEvent>> => {
  let query = supabase
    .from("email_events")
    .select(`
      *,
      mailboxes(email)
    `, { count: 'exact' })
    .eq("tenant_id", tenantId);

  // Apply filters
  if (filters.mailboxId) {
    query = query.eq("mailbox_id", filters.mailboxId);
  }
  
  if (filters.status) {
    query = query.eq("status", filters.status as any);
  }
  
  if (filters.dateFrom) {
    query = query.gte("created_at", filters.dateFrom);
  }
  
  if (filters.dateTo) {
    query = query.lte("created_at", filters.dateTo);
  }

  // Apply pagination and ordering
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  
  query = query
    .order("created_at", { ascending: false })
    .range(from, to);

  const { data, error, count } = await query;

  if (error) {
    console.error("Error fetching email events:", error);
    throw error;
  }

  // Transform the data to include joined information
  const transformedData: AnalyticsEmailEvent[] = (data || []).map((event: any) => ({
    ...event,
    mailbox_email: event.mailboxes?.email
  }));

  const total = count || 0;
  const totalPages = Math.ceil(total / pageSize);

  return {
    data: transformedData,
    total,
    page,
    pageSize,
    totalPages,
  };
};

export const fetchMailboxesForFilter = async (tenantId: string) => {
  const { data, error } = await supabase
    .from("mailboxes")
    .select("id, email, name")
    .eq("tenant_id", tenantId)
    .order("email");

  if (error) {
    console.error("Error fetching mailboxes:", error);
    throw error;
  }

  return data || [];
};